import React, { useEffect, useMemo, useState } from "react";
import ExportToolbar from "../../components/ExportToolbar";
import MetricCard from "./components/MetricCard";
import KPIPanel from "./components/KPIPanel";
import { useLocationContext } from "context/LocationContext";
import {
  flattenDistrictsWithRegions,
  fetchDistrictAprStatuses,
  summarizeAprWorkflow,
  filterDistrictStatusRows,
  buildRegionFilterOptions,
  APR_STATUS_FILTER_OPTIONS,
  getAprStatusMeta,
} from "./aprWorkflowStats";
import DistrictSubmissionTable from "./components/DistrictSubmissionTable";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const APR_YEAR_START = 2020;

const APR_YEAR_OPTIONS = (() => {
  const endYear = new Date().getFullYear();
  const options = [];
  for (let year = endYear; year >= APR_YEAR_START; year -= 1) {
    options.push({ label: String(year), value: String(year) });
  }
  return options;
})();

const AprDashboard = () => {
  const { orgTree, isOrgTreeLoading } = useLocationContext();
  const currentYear = String(new Date().getFullYear());
  const defaultYear =
    APR_YEAR_OPTIONS.find((o) => o.value === currentYear) || APR_YEAR_OPTIONS[0];

  const [reportingYear, setReportingYear] = useState(defaultYear?.value || currentYear);
  const [statusRows, setStatusRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({ percent: 0, loaded: 0, total: 0 });
  const [error, setError] = useState(null);
  const [regionFilter, setRegionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const districtRows = useMemo(() => flattenDistrictsWithRegions(orgTree), [orgTree]);

  const summary = useMemo(() => summarizeAprWorkflow(statusRows), [statusRows]);

  useEffect(() => {
    if (!districtRows.length || isOrgTreeLoading) {
      setStatusRows([]);
      setFetchProgress({ percent: 0, loaded: 0, total: 0 });
      return;
    }

    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      setStatusRows([]);
      setFetchProgress({ percent: 0, loaded: 0, total: districtRows.length });
      try {
        const rows = await fetchDistrictAprStatuses(
          districtRows,
          reportingYear,
          controller.signal,
          (progress) => {
            if (!controller.signal.aborted) setFetchProgress(progress);
          },
        );
        if (!controller.signal.aborted) setStatusRows(rows);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load APR statuses.");
        setStatusRows([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setFetchProgress({ percent: 0, loaded: 0, total: 0 });
        }
      }
    })();

    return () => controller.abort();
  }, [districtRows, reportingYear, isOrgTreeLoading]);

  useEffect(() => {
    setRegionFilter("all");
    setStatusFilter("all");
    setSearch("");
  }, [reportingYear]);

  const regionOptions = useMemo(() => buildRegionFilterOptions(statusRows), [statusRows]);

  const filteredStatusRows = useMemo(
    () => filterDistrictStatusRows(statusRows, { regionKey: regionFilter, statusFilter, search }),
    [statusRows, regionFilter, statusFilter, search],
  );

  const statusChips = useMemo(() => {
    const counts = summary.statusCounts || {};
    return [
      { key: "none", label: "Not started", count: counts.none ?? 0 },
      { key: "Start", label: "In progress", count: counts.Start ?? 0 },
      { key: "Pending", label: "Submitted (RCC)", count: counts.Pending ?? 0 },
      { key: "Completed", label: "With NDPC", count: counts.Completed ?? 0 },
      { key: "Closed", label: "Closed", count: counts.Closed ?? 0 },
    ];
  }, [summary.statusCounts]);

  const treeReady = !isOrgTreeLoading;
  const statusReady =
    treeReady && districtRows.length > 0 && !loading && statusRows.length === districtRows.length;

  const headlineMetrics = [
    {
      title: "Districts — APR started",
      value: String(summary.districtsStarted),
      unit: `/ ${summary.totalDistricts || "—"}`,
      icon: "PlayCircle",
    },
    {
      title: "Regions — APR started",
      value: String(summary.regionsStarted),
      unit: "regions",
      icon: "MapPinned",
    },
    {
      title: "Districts — submitted to RCC",
      value: String(summary.districtsSubmittedRcc),
      unit: "districts",
      icon: "Send",
    },
    {
      title: "Regions — district data with NDPC",
      value: String(summary.regionsSubmittedNdpc),
      unit: "regions",
      icon: "Landmark",
    },
    {
      title: "Closed APR reports (Districts)",
      value: String(summary.closedReports),
      unit: "districts",
      icon: "Archive",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <ExportToolbar />
      <main className="main-content">
        <div className="mb-6 md:mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              APR Dashboard
            </h1>
            <p className="text-sm md:text-base text-muted-foreground max-w-3xl">
              National KPIs and district submission tracking for the selected reporting year. Status
              matches the Progress Report workflow (Start → Pending → Completed → Closed).
            </p>
          </div>
          <div className="w-full md:w-56">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              Year under review
            </label>
            <select
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={reportingYear}
              onChange={(e) => setReportingYear(e.target.value)}
            >
              {APR_YEAR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading && districtRows.length > 0 && (
          <div className="mb-6 rounded-xl border border-border bg-card px-4 py-4 shadow-sm md:px-5">
            <div className="mb-2 flex items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">
                Loading district APR statuses for {reportingYear}…
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-foreground">
                {fetchProgress.percent}%
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
                style={{ width: `${fetchProgress.percent}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {fetchProgress.loaded} of {fetchProgress.total || districtRows.length} districts
            </p>
          </div>
        )}

        {!treeReady && (
          <p className="mb-6 text-sm text-muted-foreground">Loading organisation tree…</p>
        )}

        {treeReady && districtRows.length === 0 && (
          <p className="mb-6 rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
            No districts found in the location tree — KPIs need national district coverage in LocationContext.
          </p>
        )}

        {statusReady && (
          <div className="mb-6 flex flex-wrap gap-2">
            {statusChips.map((chip) => {
              const meta = getAprStatusMeta(chip.key === "none" ? null : chip.key);
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() =>
                    setStatusFilter((prev) => (prev === chip.key ? "all" : chip.key))
                  }
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    statusFilter === chip.key
                      ? "border-primary bg-primary/10 text-primary"
                      : meta.badgeClass
                  }`}
                >
                  {chip.label}
                  <span className="rounded-full bg-background/80 px-1.5 py-0.5 text-[10px] font-bold">
                    {chip.count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {loading && districtRows.length > 0
            ? Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="h-36 animate-pulse rounded-xl border border-border bg-card"
                />
              ))
            : headlineMetrics.map((m, i) => (
                <MetricCard key={i} {...m} />
              ))}
        </div>

        <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="rounded-xl border border-border bg-card p-4 shadow-sm md:p-6">
              <h3 className="mb-1 text-lg font-semibold text-foreground md:text-xl">
                Report performance — workflow stages
              </h3>
              <p className="mb-4 text-sm text-muted-foreground">
                District counts by APR status for {reportingYear} (snapshot).
              </p>
              <div className="h-72 w-full md:h-80">
                {loading && districtRows.length > 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    Loading chart…
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={summary.trendBars}
                      margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="stage" tick={{ fontSize: 11 }} interval={0} angle={-12} height={56} textAnchor="end" />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 8,
                          border: "1px solid var(--color-border)",
                          fontSize: 12,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="count" name="Districts" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
          <div className="lg:col-span-4">
            <KPIPanel
              title="Report KPIs"
              subtitle="Rates vs all districts in scope for the selected year"
              kpis={summary.reportKpis}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <section className="rounded-xl border border-border bg-card shadow-sm">
            <div className="border-b border-border px-4 py-3 md:px-5">
              <h3 className="text-base font-semibold text-foreground">
                Districts done (Closed)
              </h3>
              <p className="text-xs text-muted-foreground">
                {summary.districtsDoneList.length} district(s) — NDPC closed
              </p>
            </div>
            <ul className="max-h-80 divide-y divide-border overflow-y-auto">
              {!statusReady ? (
                <li className="px-4 py-6 text-sm text-muted-foreground">
                  {loading ? "Loading…" : "Load data by selecting a year with districts in scope."}
                </li>
              ) : summary.districtsDoneList.length === 0 ? (
                <li className="px-4 py-6 text-sm text-muted-foreground">No closed districts yet.</li>
              ) : (
                summary.districtsDoneList.map((d) => (
                  <li key={d.districtId} className="flex flex-col gap-0.5 px-4 py-2.5 text-sm md:flex-row md:justify-between">
                    <span className="font-medium text-foreground">{d.name}</span>
                    <span className="text-xs text-muted-foreground md:text-sm">{d.regionName}</span>
                  </li>
                ))
              )}
            </ul>
          </section>

          <section className="rounded-xl border border-border bg-card shadow-sm">
            <div className="border-b border-border px-4 py-3 md:px-5">
              <h3 className="text-base font-semibold text-foreground">
                Regions done (all districts Closed)
              </h3>
              <p className="text-xs text-muted-foreground">
                Every district under the region has APR status Closed
              </p>
            </div>
            <ul className="max-h-80 divide-y divide-border overflow-y-auto">
              {!statusReady ? (
                <li className="px-4 py-6 text-sm text-muted-foreground">
                  {loading ? "Loading…" : "No data."}
                </li>
              ) : summary.regionsDoneList.length === 0 ? (
                <li className="px-4 py-6 text-sm text-muted-foreground">No region fully closed yet.</li>
              ) : (
                summary.regionsDoneList.map((name) => (
                  <li key={name} className="px-4 py-2.5 text-sm font-medium text-foreground">
                    {name}
                  </li>
                ))
              )}
            </ul>
          </section>
        </div>

        <div className="mt-6">
          <DistrictSubmissionTable
            rows={filteredStatusRows}
            loading={loading && districtRows.length > 0}
            reportingYear={reportingYear}
            regionFilter={regionFilter}
            onRegionFilterChange={setRegionFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            search={search}
            onSearchChange={setSearch}
            regionOptions={regionOptions}
            statusOptions={APR_STATUS_FILTER_OPTIONS}
          />
        </div>
      </main>
    </div>
  );
};

export default AprDashboard;
